import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { formatPopularity, kindLabel, movies, tvshows } from "@/lib/content";

type TabKey = "all" | "movie" | "tvshow";

const TABS: { key: TabKey; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "tvshow", label: "电视剧" },
];

export default function RankingSection() {
  const [tab, setTab] = useState<TabKey>("all");

  const pool = useMemo(() => {
    const movieItems = movies.map((item) => ({
      id: item.id,
      kind: "movie" as const,
      title: item.title,
      year: item.year,
      cover: item.cover,
      rating: item.rating,
      popularity: item.popularity,
      region: item.region,
      genres: item.genres,
    }));
    const tvItems = tvshows.map((item) => ({
      id: item.id,
      kind: "tvshow" as const,
      title: item.title,
      year: item.year,
      cover: item.cover,
      rating: item.rating,
      popularity: item.popularity,
      region: item.region,
      genres: item.genres,
    }));
    return [...movieItems, ...tvItems];
  }, []);

  const ranking = useMemo(() => {
    return pool
      .filter((item) => tab === "all" || item.kind === tab)
      .sort((a, b) => b.popularity - a.popularity)
      .slice(0, 10);
  }, [pool, tab]);

  return (
    <section className="w-full px-4 py-12 md:px-6 lg:px-10 lg:py-16">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="mb-2 inline-flex items-center gap-2 text-xs font-label uppercase tracking-widest text-primary-400">
            <span className="h-1 w-6 rounded-full bg-primary-500" />
            人气榜单
          </span>
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">热门影视榜单</h2>
          <p className="mt-2 text-sm text-foreground-600">按近期点击量排序，实时反映观众的关注焦点。</p>
        </div>

        <div className="flex items-center gap-1 rounded-full border border-background-200/70 bg-background-100/70 p-1">
          {TABS.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setTab(item.key)}
              className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-label transition-colors duration-200 ${
                tab === item.key
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-600 hover:text-foreground-950"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 lg:gap-4">
        {ranking.map((item, index) => (
          <Link
            key={item.id}
            to={`/detail/${item.id}`}
            className="group flex items-center gap-4 rounded-lg border border-background-200/70 bg-background-100/60 p-3 transition-colors duration-300 hover:border-primary-500/60"
          >
            <span
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md font-heading text-lg font-bold ${
                index < 3 ? "bg-primary-500 text-background-50" : "bg-background-200/70 text-foreground-600"
              }`}
            >
              {index + 1}
            </span>
            <div className="h-20 w-14 shrink-0 overflow-hidden rounded-md bg-background-200">
              <img
                src={item.cover}
                alt={`${item.title} 海报`}
                title={`${item.title} - 夜无疆影视大全`}
                className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="line-clamp-1 font-heading text-sm font-semibold text-foreground-950 transition-colors duration-200 group-hover:text-primary-400 md:text-base">
                {item.title}
              </h3>
              <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                <span className="rounded-md bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-950">
                  {kindLabel(item.kind)}
                </span>
                <span>{item.year}</span>
                <span>{item.region}</span>
                <span className="flex items-center gap-1 text-accent-400">
                  <i className="ri-star-fill text-[10px]" />
                  {item.rating.toFixed(1)}
                </span>
              </div>
              <p className="mt-1.5 line-clamp-1 text-xs text-foreground-600">{item.summary}</p>
            </div>
            <div className="hidden shrink-0 flex-col items-end gap-1 sm:flex">
              <span className="flex items-center gap-1 text-xs text-primary-400">
                <i className="ri-fire-line" />
                {formatPopularity(item.popularity)}
              </span>
              <span className="text-[11px] text-foreground-500">点击热度</span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}